import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PartnershipsPage() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/partenariats`,
      "url": `${import.meta.env.VITE_SITE_URL}/partenariats`,
      "name": "Partenariats JLD Concept - Orange Belgique, Eneco & Smart Services Network",
      "description": "JLD Concept collabore avec Orange Belgique, Eneco et Smart Services Network pour vous offrir des services télécom et énergie de qualité exceptionnelle en Belgique.",
      "inLanguage": "fr-BE",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "mentions": [
        {
          "@type": "Organization",
          "name": "Orange Belgique",
          "description": "Leader des télécommunications en Belgique, offrant forfaits GSM, Internet haut débit et TV digitale.",
          "url": "https://www.orange.be"
        },
        {
          "@type": "Organization",
          "name": "Eneco",
          "description": "Pionnier de l'énergie verte en Belgique, fournisseur d'électricité et gaz 100% renouvelables.",
          "url": "https://www.eneco.be"
        },
        {
          "@type": "Organization",
          "name": "Smart Services Network",
          "description": "Filiale du groupe Orange créée en 2014, réseau de plus de 2000 consultants actifs en Belgique.",
          "foundingDate": "2014"
        }
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-partnerships';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-partnerships')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-block text-sm font-semibold text-orange-600 bg-orange-100 px-4 py-2 rounded-full mb-4">
              NOS PARTENAIRES
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Des partenaires de confiance
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              JLD Concept collabore avec les leaders du marché belge pour vous garantir 
              des services de qualité exceptionnelle
            </p>
          </div>
        </section>

        {/* Smart Services Network */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              <div className="w-full h-96 bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl shadow-xl flex items-center justify-center p-8">
                <img
                  src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/0c786a5140376c59ff5ed40eef5a118f.jpeg"
                  alt="Smart Services Network"
                  className="w-full h-full object-contain"
                />
              </div>

              <div>
                <div className="inline-block text-sm font-semibold text-orange-600 bg-orange-100 px-4 py-2 rounded-full mb-4">
                  NOTRE RÉSEAU
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Smart Services Network
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Smart Services Network est une filiale du groupe Orange, créée en 2014 pour 
                  révolutionner la distribution de services télécom et énergie en Belgique.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Avec plus de 2000 consultants actifs en Belgique, Smart SN représente 
                  l'excellence dans le domaine du marketing de réseau et de la distribution 
                  de services essentiels.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-orange-50 rounded-xl p-4">
                    <div className="text-3xl font-bold text-orange-600 mb-1">2014</div>
                    <div className="text-sm text-gray-600">Année de création</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="text-3xl font-bold text-green-600 mb-1">2000+</div>
                    <div className="text-sm text-gray-600">Consultants actifs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Orange Belgique */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-32 h-32 flex items-center justify-center bg-white rounded-2xl mb-6 p-4">
                  <img
                    src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/a44856ff065b1800f24e74458addede3.png"
                    alt="Orange Belgique"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-4xl font-bold mb-6">Orange Belgique - Télécom professionnel et résidentiel</h2>
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                  Leader des télécommunications en Belgique, Orange offre des solutions 
                  innovantes pour les entreprises et les particuliers avec Orange Belgique.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-smartphone-line text-orange-400"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Téléphonie Mobile</h3>
                      <p className="text-sm text-gray-400">
                        Forfaits GSM avec appels, SMS et data illimités sur le meilleur réseau de Belgique
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-wifi-line text-orange-400"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Internet Haut Débit</h3>
                      <p className="text-sm text-gray-400">
                        Connexions ultra-rapides jusqu'à 1GB pour toute la famille
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500/20 rounded-lg flex-shrink-0">
                      <i className="ri-tv-line text-orange-400"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Télévision Digitale</h3>
                      <p className="text-sm text-gray-400">
                        Plus de 70 chaînes et bouquets premium pour tous les goûts
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="w-full h-[500px] bg-cover bg-center rounded-2xl"
                style={{
                  backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7e59b524ed04b606598306922e97eb2d.jpg)',
                }}
              ></div>
            </div>
          </div>
        </section>

        {/* Eneco */}
        <section className="py-20 bg-gradient-to-br from-green-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div
                className="relative w-full h-[500px] rounded-2xl shadow-xl order-2 lg:order-1 overflow-hidden"
              >
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{
                    backgroundImage: 'url(https://readdy.ai/api/search-image?query=Eneco%20green%20renewable%20energy%20installation%20with%20solar%20panels%20and%20wind%20turbines%20in%20beautiful%20natural%20landscape%2C%20sustainable%20clean%20power%20generation%2C%20bright%20sunny%20day%20with%20blue%20sky&width=800&height=1000&seq=eneco003&orientation=portrait)',
                  }}
                ></div>
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-lg p-2">
                  <img
                    src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/e670cdcaf1ecd738ddf0cd16b324bd0d.png"
                    alt="Eneco"
                    className="h-10 w-auto object-contain"
                  />
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <div className="w-32 h-32 flex items-center justify-center bg-green-600 rounded-2xl shadow-lg mb-6 p-4">
                  <img
                    src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/e670cdcaf1ecd738ddf0cd16b324bd0d.png"
                    alt="Eneco"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Eneco</h2>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                  Pionnier de l'énergie verte en Belgique, Eneco s'engage pour un avenir 
                  durable et des solutions énergétiques respectueuses de l'environnement.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                      <i className="ri-leaf-line text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">100% Énergie Verte</h3>
                      <p className="text-sm text-gray-600">
                        Électricité et gaz provenant exclusivement de sources renouvelables
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                      <i className="ri-price-tag-3-line text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Tarifs Compétitifs</h3>
                      <p className="text-sm text-gray-600">
                        Prix transparents et stables pour maîtriser votre budget énergie
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                      <i className="ri-earth-line text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Engagement Durable</h3>
                      <p className="text-sm text-gray-600">
                        Contribution active à la transition énergétique en Belgique
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership Benefits */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">
                L'excellence de nos partenariats
              </h2>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Des collaborations stratégiques qui garantissent votre satisfaction
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                  <i className="ri-shield-star-line text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Qualité Garantie</h3>
                <p className="opacity-90">
                  Des partenaires reconnus pour leur excellence et leur fiabilité sur le marché belge
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                  <i className="ri-customer-service-2-line text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Support Dédié</h3>
                <p className="opacity-90">
                  Un service client réactif et professionnel pour répondre à toutes vos questions
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                  <i className="ri-line-chart-line text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Innovation Continue</h3>
                <p className="opacity-90">
                  Des solutions toujours à la pointe de la technologie pour votre confort
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}